/**
 * drawingSvg — the LHC Production Sheet drawing builders (Drawings step, batch 3 2026-09-12).
 *
 * Every drawing renders straight from STATE with the same engine math the views use
 * (wall polygons, cabinetWorldPos, the TRUE orthographic projection of the 3D specs for
 * elevations), restyled 1:1 to the user's reference document ("Production Sheet v 2"):
 * thin dark outlines, red dimension chains with ticks, blue wall numbers / clearances,
 * gold unit refs + SKUs, pale-blue glass, gold gola lines.
 *
 *   buildPlanDrawingSVG(state, lib, opts)             → svg string | null
 *   buildElevationDrawingSVG(state, lib, wallId, opts) → svg string | null
 *   buildIslandDrawingSVGs(state, lib, opts)          → { plan, longFace, endFace, … } | null
 *   elevationLevels(state, lib, wallId, opts)         → [{ key, label, mm, y0, y1 }]
 *   unitRefs(state, lib)                              → Map(placementId → { ref, … })
 *   wallSchedule(state, lib, wallId)                  → [{ ref, unit, sku }]
 *   islandClusters(state, lib)                        → [{ id, members, frame, … }]
 *   sheetScaleFor(needW, needH, areaW, areaH)         → 20 | 25 | 50 | 75 | 100 | 150 | 200
 *   buildPlanDXF(state, lib) / buildScheduleCSV(state, lib, units) — legacy exports (kept)
 *
 * SHEET GEOMETRY: the SVG viewBox is in REAL millimetres and sized to the drawing area on
 * the A4 sheet (opts.areaMm, mm) at the chosen scale — so a 2.4 mm font in the builder is
 * 2.4 mm on paper, and the page preview and the PDF are identical. Pure strings, no DOM.
 */
import { effDef } from "./pricing";
import { cabinetWorldPos, lhcVSpan, getWallPlacementInfo, placementAABB } from "./placement";
import { getWallPolygons } from "./wallPolygons";
import { effectiveThickness } from "./wallJoins";
import { elevMembershipOnWall, wallHeightOf, defDoorCount, defDrawerCount, getWallNumberMap, roleHigh } from "./elevation";
import { frontRowsOf } from "./frontLayout";
import { worktopRunGroups, runSeams, sheetLenM } from "./worktopRuns";
import { drawingUnitRows, dwpFmt } from "./outputs";
import { ruleVal } from "./rules";
import { surfaceMaterial, surfaceSpecOf, surfaceKey, resolveOverride } from "./surfaces";
import { buildElevationDrawing, projectSpec, projectBox, wallFrame } from "./elevationProjection";
import { buildCabinetSpecs, applyGolaRuns } from "../components/scene3d/cabinetMath";
import { resolveConfigVisual } from "../components/mcv3/configVisual";

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const N = (v) => +(+v).toFixed(1);
const MM = (m) => Math.round((+m || 0) * 1000);

/* ── the reference palette (sampled from the source HTML, not eyeballed) ─── */
export const SHEET_INK = "#1a1a1a";
export const SHEET_RED = "#cc2a2a";
export const SHEET_BLUE = "#1763c7";
export const SHEET_GOLD = "#a07f47";
export const SHEET_GOLD_DK = "#7c6234";
const GREY = "#cbc4b4";       // thin guide lines
const PANE = "rgba(99,140,170,.12)"; // glass / window pane fill
const FILLER = "#eef0f4";
const FONT = "'Raleway', sans-serif";

/* line weights (mm on the sheet) and font sizes (mm on the sheet) */
const LW = { wall: 0.40, cab: 0.34, thin: 0.12, dim: 0.17, floor: 0.48, gola: 0.34, guide: 0.11 };
const FS = { dim: 2.5, dimSm: 2.2, total: 2.6, level: 1.9, ref: 2.4, sku: 1.3, label: 2.4, labelSm: 1.9, island: 3.2, wallNo: 2.8, caption: 2.4, ref2: 1.6 };

/* the drawing areas on the A4-landscape sheet (mm) — sheets.css gives the SVG boxes
 * exactly these sizes, which is what makes the printed "SCALE 1:nn" truthful */
export const PLAN_AREA_MM = { w: 201, h: 140 };
export const ELEV_AREA_MM = { w: 201, h: 146 };
export const ISLAND_PLAN_AREA_MM = { w: 201, h: 68 };
export const ISLAND_ELEV_A_AREA_MM = { w: 114, h: 66 };
export const ISLAND_ELEV_B_AREA_MM = { w: 66, h: 66 };
/** plan chains print a GAP label only from this width up (the elevation keeps every gap) */
export const PLAN_MIN_GAP_LABEL_MM = 60;

export const SHEET_SCALES = [20, 25, 50, 75, 100, 150, 200];
/** rough Raleway width of an upper-case label on the sheet (mm): ~0.66 em per glyph + letter-spacing */
export function textWidthMm(s, fsMm, lsMm = 0) {
  const str = String(s == null ? "" : s);
  if (!str.length) return 0;
  let w = 0;
  for (const ch of str) w += /[ ·.:,|'`]/.test(ch) ? 0.3 : /[il1I]/.test(ch) ? 0.36 : /[mwMW]/.test(ch) ? 0.9 : 0.66;
  return w * fsMm + Math.max(0, str.length - 1) * lsMm;
}
/** the smallest standard scale denominator at which needW×needH (mm) fits areaW×areaH (mm) */
export function sheetScaleFor(needWmm, needHmm, areaWmm, areaHmm) {
  const aw = Math.max(1, areaWmm || 1), ah = Math.max(1, areaHmm || 1);
  for (const den of SHEET_SCALES) {
    if ((needWmm || 0) / den <= aw && (needHmm || 0) / den <= ah) return den;
  }
  return SHEET_SCALES[SHEET_SCALES.length - 1];
}

/** length label in the sheet's units (mm → "600", cm → "60" / "60.5") */
export function fmtLen(mm, units) {
  const v = Math.round(+mm || 0);
  if (units === "cm") { const c = Math.round(v / 10 * 10) / 10; return String(c); }
  if (units === "m") return (v / 1000).toFixed(2);
  return String(v);
}

/* ── primitives (all coordinates in drawing mm; sizes in sheet mm × den) ──── */
function text(x, y, s, fs, fill, o = {}) {
  const a = [];
  a.push(`x="${N(x)}" y="${N(y)}" font-size="${N(fs)}" fill="${fill}"`);
  a.push(`text-anchor="${o.anchor || "middle"}"`);
  if (o.weight) a.push(`font-weight="${o.weight}"`);
  if (o.ls) a.push(`letter-spacing="${N(o.ls)}"`);
  if (o.rot) a.push(`transform="rotate(${N(o.rot)} ${N(x)} ${N(y)})"`);
  if (o.italic) a.push(`font-style="italic"`);
  return `<text ${a.join(" ")}${o.extra || ""}>${esc(s)}</text>`;
}
function line(x1, y1, x2, y2, stroke, w, extra = "") {
  return `<line x1="${N(x1)}" y1="${N(y1)}" x2="${N(x2)}" y2="${N(y2)}" stroke="${stroke}" stroke-width="${N(w)}"${extra}/>`;
}
function rect(x, y, w, h, stroke, sw, fill = "none", extra = "") {
  return `<rect x="${N(x)}" y="${N(y)}" width="${N(Math.max(0, w))}" height="${N(Math.max(0, h))}" fill="${fill}"${stroke ? ` stroke="${stroke}" stroke-width="${N(sw)}"` : ""}${extra}/>`;
}
function poly(pts, stroke, sw, fill = "none", extra = "") {
  return `<polygon points="${pts.map(([x, y]) => `${N(x)},${N(y)}`).join(" ")}" fill="${fill}" stroke="${stroke}" stroke-width="${N(sw)}"${extra}/>`;
}
/** a red dimension line with end ticks and a centred label (horizontal or vertical) */
function dimLine(x1, y1, x2, y2, label, den, o = {}) {
  const t = 1.0 * den; // tick half-length (mm sheet)
  const vertical = Math.abs(x2 - x1) < 1e-6;
  let g = `<g stroke="${SHEET_RED}" stroke-width="${N(LW.dim * den)}" fill="none">`;
  g += line(x1, y1, x2, y2, SHEET_RED, LW.dim * den);
  if (vertical) { g += line(x1 - t, y1, x1 + t, y1, SHEET_RED, LW.dim * den); g += line(x2 - t, y2, x2 + t, y2, SHEET_RED, LW.dim * den); }
  else { g += line(x1, y1 - t, x1, y1 + t, SHEET_RED, LW.dim * den); g += line(x2, y2 - t, x2, y2 + t, SHEET_RED, LW.dim * den); }
  g += `</g>`;
  if (label != null && label !== "") {
    const fs = (o.fs || FS.dim) * den;
    const off = (o.offset != null ? o.offset : 0.9) * den;
    if (vertical) {
      const cy = (y1 + y2) / 2;
      const side = o.side === "right" ? 1 : -1; // label left of the line by default
      const x = x1 + side * off;
      g += text(x, cy, label, fs, o.fill || SHEET_INK, { rot: side < 0 ? -90 : 90, weight: o.weight });
    } else {
      const cx = (x1 + x2) / 2;
      const y = o.below ? y1 + off + fs * 0.8 : y1 - off;
      g += text(cx, y, label, fs, o.fill || SHEET_INK, { weight: o.weight });
    }
  }
  return g;
}
/** ticks + segment labels along one chain line (breakpoints already sorted, drawing mm) */
function dimChain(bps, y, den, labelOf, o = {}) {
  if (bps.length < 2) return "";
  const t = 1.0 * den, fs = (o.fs || FS.dimSm) * den;
  let g = `<g stroke="${SHEET_RED}" stroke-width="${N(LW.dim * den)}" fill="none">`;
  g += line(bps[0], y, bps[bps.length - 1], y, SHEET_RED, LW.dim * den);
  bps.forEach((x) => { g += line(x, y - t, x, y + t, SHEET_RED, LW.dim * den); });
  g += `</g>`;
  for (let i = 0; i + 1 < bps.length; i++) {
    const a = bps[i], b = bps[i + 1];
    if (b - a < 0.5) continue;
    const l = labelOf(a, b, i);
    if (!l) continue;
    g += text((a + b) / 2, o.below ? y + 0.9 * den + fs * 0.8 : y - 0.9 * den, l.text, fs, l.fill || SHEET_INK, { weight: l.weight });
  }
  return g;
}
function svgOpen(vx, vy, vw, vh, den, extra = "") {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${N(vx)} ${N(vy)} ${N(vw)} ${N(vh)}" preserveAspectRatio="xMidYMid meet" text-rendering="geometricPrecision" font-family="${FONT}" style="width:100%;height:100%;display:block" data-scale="${den}"${extra}>`;
}
/** viewBox origin so that content [c0,c1] with sheet-margins (mL,mR in mm) is centred in an area of areaW sheet-mm */
function vbOrigin(c0, c1, mL, mR, areaW, den) {
  const content = (c1 - c0) + (mL + mR) * den;
  return c0 - mL * den - (areaW * den - content) / 2;
}

/* ── classification helpers (shared by plan / elevation / island / schedules) ── */
const cfgHas = (p, k) => { const v = p && p.config && p.config[k]; return Array.isArray(v) ? v.length > 0 : !!v; };
/** the appliance kind of a placement: hob | sink | dw | fridge | hood | oven | appliance | null */
export function applianceKind(p, def) {
  if (!def) return null;
  const st = String(def.subtype || "").toLowerCase();
  const code = String(def.code || "").toUpperCase();
  const name = String(def.name || "").toLowerCase();
  if (st === "hob" || cfgHas(p, "eqHobs") || /(^|-)HOB(-|$)/.test(code) || /\bhob\b/.test(name)) return "hob";
  if (st === "sink" || cfgHas(p, "eqSinks") || /(^|-)SK(-|$)|D\dSK(-|$)|SINK/.test(code) || /\bsink\b/.test(name)) return "sink";
  if (st === "dishwasher" || cfgHas(p, "eqDishwashers") || /(^|-)DW(-|$)|DISH/.test(code) || /dishwasher/.test(name)) return "dw";
  if (st === "fridge" || cfgHas(p, "eqFridges") || (def.role === "cabinet-tall" && /FR/.test(code)) || /fridge|freezer/.test(name)) return "fridge";
  if (st === "hood" || cfgHas(p, "eqHoods") || /HOOD|(^|-)HD-/.test(code) || /\bhood\b|extractor/.test(name)) return "hood";
  if (st === "oven" || cfgHas(p, "eqOvens") || /D\dOV(-|$)|(^|-)OV(EN)?(-|$)/.test(code) || /\boven\b/.test(name)) return "oven";
  if (def.role === "appliance") return st || "appliance";
  return null;
}
const APPLIANCE_TEXT = { hob: "HOB", sink: "SINK", dw: "DW", fridge: "FRIDGE / FREEZER", hood: "HOOD", oven: "OVEN" };
function isTallDef(def) {
  return def.role === "cabinet-tall" || (def.role === "appliance" && (def.height || 0) >= 1.2);
}
function isGlassFront(p, def, blob) {
  if (/(^|-)D\dGL|GLASS/i.test(String(def.code || "")) || /\bglass\b/i.test(String(def.name || ""))) return true;
  try { const v = p && p.config ? resolveConfigVisual(blob || {}, p.type, p.config) : null; return !!(v && v.frontGlass); } catch (e) { return false; }
}

/** Cabinet plan corners: center ± dir·w/2 ± n·d/2 (world m). */
function planCorners(p, def, walls) {
  const pos = cabinetWorldPos(p, def, walls);
  if (!pos) return null;
  const hw = (def.width || 0) / 2, hd = (def.depth || 0) / 2;
  const dx = pos.dirX, dz = pos.dirZ, nx = pos.nx, nz = pos.nz;
  return {
    pos,
    pts: [
      [pos.cx - dx * hw - nx * hd, pos.cz - dz * hw - nz * hd],
      [pos.cx + dx * hw - nx * hd, pos.cz + dz * hw - nz * hd],
      [pos.cx + dx * hw + nx * hd, pos.cz + dz * hw + nz * hd],
      [pos.cx - dx * hw + nx * hd, pos.cz - dz * hw + nz * hd],
    ],
  };
}

/** Opening world quad across the wall thickness. */
function openingQuad(op, walls) {
  const wall = (walls || []).find((w) => w.id === op.wallId);
  if (!wall) return null;
  const info = getWallPlacementInfo(wall, walls);
  if (!info) return null;
  const t2 = effectiveThickness(wall) / 2;
  const ax = wall.start[0] + info.dirX * op.u, az = wall.start[1] + info.dirZ * op.u;
  const bx = wall.start[0] + info.dirX * (op.u + op.width), bz = wall.start[1] + info.dirZ * (op.u + op.width);
  const px = -info.dirZ, pz = info.dirX;
  return {
    a: [ax, az], b: [bx, bz], px, pz, t2, info,
    pts: [
      [ax + px * t2, az + pz * t2], [bx + px * t2, bz + pz * t2],
      [bx - px * t2, bz - pz * t2], [ax - px * t2, az - pz * t2],
    ],
  };
}

/* ── wall order + unit refs (ONE source of truth for the sheets and the schedules) ── */
/** walls sorted by the editor's spatial wall number (getWallNumberMap) */
export function orderedWalls(walls) {
  const ws = (walls || []).slice();
  const num = getWallNumberMap(ws);
  return ws.sort((a, b) => (num.get(a.id) || 0) - (num.get(b.id) || 0));
}
export function wallNumberOf(walls, wallId) {
  return getWallNumberMap(walls || []).get(wallId) || 0;
}

/** Island clusters: free-standing (island:true) placements grouped by touching footprints.
 *  Each cluster carries an oriented frame (dir along, n = facing) and its u/q extents. */
export function islandClusters(state, lib) {
  const walls = (state && state.walls) || [];
  const items = [];
  ((state && state.placements) || []).forEach((p) => {
    if (!p || !p.island) return;
    const def = effDef(p, lib);
    if (!def) return;
    const box = placementAABB(p, def, walls);
    const c = planCorners(p, def, walls);
    if (!box || !c) return;
    items.push({ p, def, box, pts: c.pts, pos: c.pos });
  });
  if (!items.length) return [];
  const TOL = 0.06;
  const parent = items.map((_, i) => i);
  const find = (i) => (parent[i] === i ? i : (parent[i] = find(parent[i])));
  const touch = (a, b) => a.box.minX <= b.box.maxX + TOL && b.box.minX <= a.box.maxX + TOL
    && a.box.minZ <= b.box.maxZ + TOL && b.box.minZ <= a.box.maxZ + TOL;
  for (let i = 0; i < items.length; i++) for (let j = i + 1; j < items.length; j++) if (touch(items[i], items[j])) parent[find(i)] = find(j);
  const groups = new Map();
  items.forEach((it, i) => { const r = find(i); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(it); });
  const out = [];
  [...groups.values()].sort((a, b) => a[0].box.minX - b[0].box.minX || a[0].box.minZ - b[0].box.minZ).forEach((members, gi) => {
    const first = members[0].pos;
    const nx = first.nx, nz = first.nz;
    const dirX = nz, dirZ = -nx;                       // right-handed: n = (-dirZ, dirX)
    let u0 = Infinity, u1 = -Infinity, q0 = Infinity, q1 = -Infinity;
    members.forEach((m) => m.pts.forEach(([x, z]) => {
      const u = x * dirX + z * dirZ, q = x * nx + z * nz;
      u0 = Math.min(u0, u); u1 = Math.max(u1, u); q0 = Math.min(q0, q); q1 = Math.max(q1, q);
    }));
    members.sort((a, b) => (a.pos.cx * dirX + a.pos.cz * dirZ) - (b.pos.cx * dirX + b.pos.cz * dirZ));
    out.push({
      id: "IS" + String(gi + 1).padStart(2, "0"), no: gi + 1, members,
      frame: { dirX, dirZ, nx, nz, sx: dirX * u0 + nx * q0, sz: dirZ * u0 + nz * q0 },
      u0, u1, q0, q1, lengthM: u1 - u0, depthM: q1 - q0,
    });
  });
  return out;
}

/** unitRefs(state, lib) → Map(placementId → { ref, prefix, n, wallId, wallNo, sku, name, role, kind, island })
 *  W = wall units, B = base, T = tall/columns, IS = island (per cluster), numbered left→right
 *  per wall, continuing across walls in wall order (W01…W08 across the project). */
export function unitRefs(state, lib) {
  const out = new Map();
  const walls = (state && state.walls) || [];
  const placements = (state && state.placements) || [];
  const counters = { W: 0, B: 0, T: 0 };
  const prefixOf = (def) => (roleHigh(def.role) ? "W" : isTallDef(def) ? "T" : def.role === "cabinet-base" ? "B" : (def.role === "appliance" ? "B" : null));
  const skip = (p, def) => { const k = applianceKind(p, def); return def.role === "appliance" && (k === "hob" || k === "sink" || k === "hood" || k === "tap"); };
  orderedWalls(walls).forEach((wall) => {
    const wallNo = wallNumberOf(walls, wall.id);
    placements
      .filter((p) => p && p.wallId === wall.id && !p.island)
      .map((p) => ({ p, def: effDef(p, lib) }))
      .filter((x) => x.def)
      .sort((a, b) => ((a.p.absLeft || 0) - (b.p.absLeft || 0)) || String(a.def.role).localeCompare(String(b.def.role)))
      .forEach(({ p, def }) => {
        if (skip(p, def)) return;
        const prefix = prefixOf(def);
        if (!prefix) return;
        counters[prefix] += 1;
        out.set(p.id, { ref: prefix + String(counters[prefix]).padStart(2, "0"), prefix, n: counters[prefix], wallId: wall.id, wallNo, sku: def.code || "—", name: def.name || def.code || p.type, role: def.role, kind: applianceKind(p, def), island: false });
      });
  });
  islandClusters(state, lib).forEach((cl) => {
    cl.members.forEach(({ p, def }, k) => {
      out.set(p.id, { ref: cl.id, prefix: "IS", n: cl.no, sub: k + 1, wallId: null, wallNo: 0, sku: def.code || "—", name: def.name || def.code || p.type, role: def.role, kind: applianceKind(p, def), island: true });
    });
  });
  return out;
}

/** the UNIT SCHEDULE rows for one wall: REF / UNIT / SKU, then EXT / WIN / DR rows */
export function wallSchedule(state, lib, wallId) {
  const refs = unitRefs(state, lib);
  const rows = [];
  const placements = (state && state.placements) || [];
  const onWall = placements.filter((p) => p && p.wallId === wallId && !p.island).map((p) => ({ p, def: effDef(p, lib) })).filter((x) => x.def)
    .sort((a, b) => (a.p.absLeft || 0) - (b.p.absLeft || 0));
  // reference order: wall units, the extractor, tall units, base units, then openings
  const group = (pred) => onWall.forEach(({ p }) => { const r = refs.get(p.id); if (r && pred(r)) rows.push({ ref: r.ref, unit: r.name, sku: r.sku, id: p.id }); });
  group((r) => r.prefix === "W");
  onWall.forEach(({ p, def }) => {
    if (applianceKind(p, def) === "hood") rows.push({ ref: "EXT", unit: def.role === "appliance" ? (def.name || "Extractor hood") : "Extractor hood · " + (def.name || ""), sku: def.role === "appliance" ? (def.code || "—") : "—", id: p.id + ":ext" });
  });
  group((r) => r.prefix === "T");
  group((r) => r.prefix !== "W" && r.prefix !== "T");
  ((state && state.wallOpenings) || []).filter((o) => o.wallId === wallId).forEach((op) => {
    const w = MM(op.width), h = MM(op.height || (op.type === "door" ? 2.1 : 1.5));
    rows.push(op.type === "door"
      ? { ref: "DR", unit: `Door opening ${w}×${h}`, sku: "—", id: op.id }
      : { ref: "WIN", unit: `Window opening ${w}×${h}`, sku: "—", id: op.id });
  });
  return rows;
}

/* ── worktop spec for a placement (technician material → override layer) ── */
function worktopSpecFor(state, blob, p, members) {
  const kw = (state && state.kitchenWizard) || {};
  const name = (p && p.config && p.config.countertop) || kw.worktop || null;
  const mat = surfaceMaterial(blob, "worktop", name);
  const store = kw.surfaceOverrides || null;
  const ids = members || (p ? [p.id] : []);
  let over = null;
  try { over = store ? resolveOverride(store, surfaceKey("wt", p && p.wallId, ids), ids) : null; } catch (e) { over = null; }
  return { spec: surfaceSpecOf(mat, "worktop", over), over: over || {} };
}

/* ═══════════════════════════════════════════════════════════════════════════
 * PLAN VIEW
 * ═══════════════════════════════════════════════════════════════════════════ */
export function buildPlanDrawingSVG(state, lib, opts = {}) {
  const units = opts.units || "mm";
  const area = opts.areaMm || PLAN_AREA_MM;
  const walls = (state && state.walls) || [];
  const placements = (state && state.placements) || [];
  if (!walls.length && !placements.length) return null;

  const polys = getWallPolygons(walls);
  const numMap = getWallNumberMap(walls);
  const refs = unitRefs(state, lib);
  const cabs = [];
  placements.forEach((p) => {
    const def = effDef(p, lib);
    if (!def) return;
    const c = planCorners(p, def, walls);
    if (!c) return;
    const [y0] = lhcVSpan(p, def);
    cabs.push({ p, def, pts: c.pts, pos: c.pos, overhead: y0 > 0.3 && !isTallDef(def), kind: applianceKind(p, def), island: !!p.island });
  });
  const clusters = islandClusters(state, lib);

  // world bounds (m)
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  const eat = (x, z) => { if (x < minX) minX = x; if (x > maxX) maxX = x; if (z < minZ) minZ = z; if (z > maxZ) maxZ = z; };
  polys.forEach((r) => r.polygon.forEach(([x, z]) => eat(x, z)));
  cabs.forEach((c) => c.pts.forEach(([x, z]) => eat(x, z)));
  if (!isFinite(minX)) return null;

  const X = (x) => x * 1000, Y = (z) => z * 1000;
  const spanW = (maxX - minX) * 1000, spanH = (maxZ - minZ) * 1000;
  const M = { l: 24, r: 24, t: 22, b: 24 };            // sheet-mm margins for the dimension lanes
  const den = sheetScaleFor(spanW, spanH, area.w - M.l - M.r, area.h - M.t - M.b);
  const vx = vbOrigin(X(minX), X(maxX), M.l, M.r, area.w, den);
  const vy = vbOrigin(Y(minZ), Y(maxZ), M.t, M.b, area.h, den);
  const pt = (q) => q.map(([x, z]) => [X(x), Y(z)]);
  const wallOutward = (wall) => { const i = getWallPlacementInfo(wall, walls); return i ? { x: -i.nx, z: -i.nz, info: i } : null; };
  const readable = (deg) => { let d = ((deg % 360) + 360) % 360; if (d > 90 && d < 270) d -= 180; return d; };

  let g = "";
  /* walls */
  polys.forEach((r) => { g += poly(pt(r.polygon), SHEET_INK, LW.wall * den, "#ffffff"); });

  /* openings: door swing + window double line */
  ((state && state.wallOpenings) || []).forEach((op) => {
    const q = openingQuad(op, walls);
    if (!q) return;
    g += poly(pt(q.pts), "none", 0, "#ffffff");
    const inward = { x: q.info.nx, z: q.info.nz };
    if (op.type === "door") {
      const hinge = op.hinge === "right" ? q.b : q.a, other = op.hinge === "right" ? q.a : q.b;
      const ix = X(hinge[0]), iz = Y(hinge[1]);
      // jambs across the wall thickness
      g += line(X(q.pts[0][0]), Y(q.pts[0][1]), X(q.pts[3][0]), Y(q.pts[3][1]), SHEET_INK, LW.cab * den);
      g += line(X(q.pts[1][0]), Y(q.pts[1][1]), X(q.pts[2][0]), Y(q.pts[2][1]), SHEET_INK, LW.cab * den);
      // leaf standing open into the room + dashed quarter swing
      const lx = X(hinge[0] + inward.x * op.width), lz = Y(hinge[1] + inward.z * op.width);
      g += line(ix, iz, lx, lz, SHEET_INK, LW.cab * den);
      const ex = X(other[0]), ez = Y(other[1]);
      const cross = (other[0] - hinge[0]) * inward.z - (other[1] - hinge[1]) * inward.x;
      g += `<path d="M ${N(lx)} ${N(lz)} A ${N(op.width * 1000)} ${N(op.width * 1000)} 0 0 ${cross > 0 ? 1 : 0} ${N(ex)} ${N(ez)}" fill="none" stroke="${SHEET_INK}" stroke-width="${N(LW.dim * den)}" stroke-dasharray="${N(0.8 * den)} ${N(0.55 * den)}"/>`;
    } else {
      g += line(X(q.pts[0][0]), Y(q.pts[0][1]), X(q.pts[3][0]), Y(q.pts[3][1]), SHEET_INK, LW.cab * den);
      g += line(X(q.pts[1][0]), Y(q.pts[1][1]), X(q.pts[2][0]), Y(q.pts[2][1]), SHEET_INK, LW.cab * den);
      const off = Math.min(q.t2 * 0.35, 0.03);
      [-off, off].forEach((d) => {
        g += line(X(q.a[0] + q.px * d), Y(q.a[1] + q.pz * d), X(q.b[0] + q.px * d), Y(q.b[1] + q.pz * d), SHEET_INK, LW.cab * 0.8 * den);
      });
    }
  });

  /* cabinets: base/tall thin rectangles; overhead units dashed (plan convention); islands via their cluster */
  cabs.forEach(({ p, def, pts, pos, overhead, kind, island }) => {
    const dash = overhead ? ` stroke-dasharray="${N(0.9 * den)} ${N(0.6 * den)}"` : "";
    g += poly(pt(pts), SHEET_INK, (overhead ? LW.thin : LW.cab) * den, "none", dash);
    const cx = X(pos.cx), cz = Y(pos.cz);
    const along = readable(Math.atan2(pos.dirZ, pos.dirX) * 180 / Math.PI);
    const w = (def.width || 0) * 1000, d = (def.depth || 0) * 1000;
    if (kind && APPLIANCE_TEXT[kind] && kind !== "hood") {
      if (kind === "hob" || kind === "dw" || kind === "oven") {
        const iw = w * 0.86, id = d * 0.72;
        const c = Math.cos(Math.atan2(pos.dirZ, pos.dirX)), s = Math.sin(Math.atan2(pos.dirZ, pos.dirX));
        const corner = (u, v) => [cx + u * c - v * s, cz + u * s + v * c];
        g += poly([corner(-iw / 2, -id / 2), corner(iw / 2, -id / 2), corner(iw / 2, id / 2), corner(-iw / 2, id / 2)], SHEET_GOLD, LW.dim * den, "none", ` stroke-dasharray="${N(0.8 * den)} ${N(0.5 * den)}"`);
        g += text(cx, cz + FS.label * den * 0.35, APPLIANCE_TEXT[kind], FS.label * den, SHEET_GOLD, { rot: along, ls: 0.1 * den });
      } else if (kind === "sink") {
        g += `<ellipse cx="${N(cx)}" cy="${N(cz)}" rx="${N(Math.min(w, 600) * 0.33)}" ry="${N(Math.min(d, 600) * 0.25)}" fill="#ffffff" stroke="${SHEET_INK}" stroke-width="${N(LW.thin * 1.5 * den)}"/>`;
        g += text(cx, cz + d * 0.5 - 1.2 * den, "SINK", FS.labelSm * den, SHEET_BLUE, { rot: along });
      } else if (kind === "fridge") {
        const vert = d > w;
        g += text(cx, cz + FS.labelSm * den * 0.35, APPLIANCE_TEXT.fridge, FS.labelSm * den, SHEET_INK, { rot: vert ? readable(along + 90) : along, ls: 0.06 * den });
      }
    } else if (!overhead && !island) {
      const r = refs.get(p.id);
      if (r && w >= 280) g += text(cx, cz + FS.ref2 * den * 0.35, r.ref, FS.ref2 * den, SHEET_GOLD, { rot: along });
    }
  });

  /* islands: worktop outline (red) + "ISLAND · ISnn" + dims + walkway clearances */
  const wallBoxes = polys.map((r) => { let a = Infinity, b = -Infinity, c = Infinity, d = -Infinity; r.polygon.forEach(([x, z]) => { a = Math.min(a, x); b = Math.max(b, x); c = Math.min(c, z); d = Math.max(d, z); }); return { minX: a, maxX: b, minZ: c, maxZ: d }; });
  const obstacles = cabs.filter((c) => !c.island && !c.overhead).map((c) => placementAABB(c.p, c.def, walls)).filter(Boolean).concat(wallBoxes);
  const rayHit = (ox, oz, dx, dz, boxes) => {
    let best = Infinity;
    boxes.forEach((b) => {
      // slab test: distance t along the ray to enter the box (ignoring boxes we start inside)
      const tx = dx !== 0 ? [(b.minX - ox) / dx, (b.maxX - ox) / dx] : (ox >= b.minX && ox <= b.maxX ? [-Infinity, Infinity] : null);
      const tz = dz !== 0 ? [(b.minZ - oz) / dz, (b.maxZ - oz) / dz] : (oz >= b.minZ && oz <= b.maxZ ? [-Infinity, Infinity] : null);
      if (!tx || !tz) return;
      const t0 = Math.max(Math.min(...tx), Math.min(...tz)), t1 = Math.min(Math.max(...tx), Math.max(...tz));
      if (t1 < t0 || t1 < 0) return;
      if (t0 > 0.005 && t0 < best) best = t0;
    });
    return best;
  };
  clusters.forEach((cl) => {
    const f = cl.frame;
    const { spec } = worktopSpecFor(state, opts.blob, cl.members[0].p, cl.members.map((m) => m.p.id));
    const ovF = spec.overhangFrontMm / 1000, ovB = spec.overhangBackMm / 1000, ovL = spec.overhangSideLMm / 1000, ovR = spec.overhangSideRMm / 1000;
    const W = (u, q) => [X(f.dirX * u + f.nx * q), Y(f.dirZ * u + f.nz * q)];
    const top = [W(cl.u0 - ovL, cl.q0 - ovB), W(cl.u1 + ovR, cl.q0 - ovB), W(cl.u1 + ovR, cl.q1 + ovF), W(cl.u0 - ovL, cl.q1 + ovF)];
    g += poly(top, SHEET_RED, LW.dim * den, "none");
    const cx = (top[0][0] + top[2][0]) / 2, cz = (top[0][1] + top[2][1]) / 2;
    const along = readable(Math.atan2(f.dirZ, f.dirX) * 180 / Math.PI);
    const lenMm = (cl.u1 - cl.u0 + ovL + ovR) * 1000, depMm = (cl.q1 - cl.q0 + ovF + ovB) * 1000;
    const lane = 6 * den / 1000;
    const midU = (cl.u0 + cl.u1) / 2;
    // "ISLAND · ISnn": centred INSIDE the outline when the label fits (sheet mm × den = drawing mm),
    // else BELOW the island — beyond the length-dimension lane so it never sits on a number
    const label = `ISLAND · ${cl.id}`;
    const labelW = textWidthMm(label, FS.island, 0.2) * den, labelH = FS.island * 1.3 * den;
    const fitsInside = labelW <= lenMm - 3 * den && labelH <= depMm - 2 * den;
    if (fitsInside) {
      g += text(cx, cz + FS.island * den * 0.35, label, FS.island * den, SHEET_INK, { rot: along, ls: 0.2 * den, extra: ` data-island-label="inside"` });
    } else {
      const lp = W(midU, cl.q1 + ovF + lane + 5.5 * den / 1000);
      g += text(lp[0], lp[1] + FS.island * den * 0.35, label, FS.island * den, SHEET_INK, { rot: along, ls: 0.2 * den, extra: ` data-island-label="below"` });
    }
    // length dim in front of the island, depth dim at its right end
    const a = W(cl.u0 - ovL, cl.q1 + ovF + lane), b = W(cl.u1 + ovR, cl.q1 + ovF + lane);
    const c = W(cl.u1 + ovR + lane, cl.q0 - ovB), d = W(cl.u1 + ovR + lane, cl.q1 + ovF);
    g += obliqueDim(a, b, fmtLen(lenMm, units), den, along, 1);
    g += obliqueDim(c, d, fmtLen(depMm, units), den, readable(along + 90), 1);
    // walkway clearances front / back (blue) to the nearest run or wall — measured at the
    // island's middle but DRAWN outside its footprint (past the freer end) so the vertical
    // line never crosses the label or the length number; the number sits on the outer side
    const freeL = rayHit(f.dirX * cl.u0 + f.nx * (cl.q0 + cl.q1) / 2, f.dirZ * cl.u0 + f.nz * (cl.q0 + cl.q1) / 2, -f.dirX, -f.dirZ, obstacles);
    const freeR = rayHit(f.dirX * cl.u1 + f.nx * (cl.q0 + cl.q1) / 2, f.dirZ * cl.u1 + f.nz * (cl.q0 + cl.q1) / 2, f.dirX, f.dirZ, obstacles);
    const useLeft = (isFinite(freeL) ? freeL : 99) >= (isFinite(freeR) ? freeR : 99);
    const sideU = useLeft ? cl.u0 - ovL - 4 * den / 1000 : cl.u1 + ovR + lane + 5 * den / 1000;   // right: clear of the depth dim
    const awayX = (useLeft ? -1 : 1) * f.dirX, awayZ = (useLeft ? -1 : 1) * f.dirZ;
    [[1, cl.q1 + ovF], [-1, cl.q0 - ovB]].forEach(([sgn, qEdge]) => {
      const ox = f.dirX * midU + f.nx * qEdge, oz = f.dirZ * midU + f.nz * qEdge;
      const dist = rayHit(ox, oz, f.nx * sgn, f.nz * sgn, obstacles);
      if (!isFinite(dist) || dist < 0.05 || dist > 6) return;
      const sx = f.dirX * sideU + f.nx * qEdge, sz = f.dirZ * sideU + f.nz * qEdge;
      const p1 = [X(sx), Y(sz)], p2 = [X(sx + f.nx * sgn * dist), Y(sz + f.nz * sgn * dist)];
      const ux = p2[0] - p1[0], uy = p2[1] - p1[1], L = Math.hypot(ux, uy) || 1;
      const px = -uy / L, py = ux / L;                                   // obliqueDim's label side
      const sideSign = (px * awayX + py * awayZ) >= 0 ? 1 : -1;
      g += obliqueDim(p1, p2, fmtLen(dist * 1000, units), den, readable(along + 90), sideSign, SHEET_BLUE, ` data-clearance="${sgn > 0 ? "front" : "back"}"`);
    });
  });

  /* per-wall unit chains (outside the wall) + blue wall numbers */
  walls.forEach((wall) => {
    const out = wallOutward(wall);
    if (!out) return;
    const info = out.info, len = info.len;
    const t2 = effectiveThickness(wall) / 2;
    const along = readable(Math.atan2(info.dirZ, info.dirX) * 180 / Math.PI);
    const no = numMap.get(wall.id);
    const onWall = cabs.filter((c) => c.p.wallId === wall.id && !c.island);
    const lower = onWall.filter((c) => !c.overhead);
    const row = lower.length ? lower : onWall;
    const bps = new Set([0, len]);
    row.forEach(({ p, def }) => { const a = p.absLeft || 0; bps.add(Math.max(0, a)); bps.add(Math.min(len, a + (def.width || 0))); });
    const openings = ((state && state.wallOpenings) || []).filter((o) => o.wallId === wall.id);
    openings.forEach((o) => { bps.add(Math.max(0, o.u)); bps.add(Math.min(len, o.u + o.width)); });
    const isUnit = (a, b) => row.some(({ p, def }) => (p.absLeft || 0) <= a + 0.004 && (p.absLeft || 0) + (def.width || 0) >= b - 0.004);
    const isOpening = (a, b) => openings.some((o) => o.u <= a + 0.004 && o.u + o.width >= b - 0.004);
    const sorted = [...bps].sort((x, y) => x - y).filter((v, i, arr) => i === 0 || v - arr[i - 1] > 0.004);
    if (row.length || openings.length) {
      const lane = t2 + 6 * den / 1000;
      const P = (u) => [X(wall.start[0] + info.dirX * u + out.x * lane), Y(wall.start[1] + info.dirZ * u + out.z * lane)];
      const p0 = P(0), p1 = P(len);
      const tick = 1.0 * den;
      let cg = `<g stroke="${SHEET_RED}" stroke-width="${N(LW.dim * den)}" fill="none">` + line(p0[0], p0[1], p1[0], p1[1], SHEET_RED, LW.dim * den);
      sorted.forEach((u) => { const q = P(u); cg += line(q[0] - out.x * tick, q[1] - out.z * tick, q[0] + out.x * tick, q[1] + out.z * tick, SHEET_RED, LW.dim * den); });
      cg += `</g>`;
      for (let i = 0; i + 1 < sorted.length; i++) {
        const a = sorted[i], b = sorted[i + 1];
        if (b - a < 0.02) continue;
        // a plan chain prints gaps ≥ PLAN_MIN_GAP_LABEL_MM only (the tick stays); units and openings always print
        if (!isUnit(a, b) && !isOpening(a, b) && (b - a) * 1000 < PLAN_MIN_GAP_LABEL_MM - 0.5) continue;
        const q = P((a + b) / 2);
        const lx = q[0] + out.x * 1.0 * den, lz = q[1] + out.z * 1.0 * den;
        const fs = FS.dimSm * den;
        cg += text(lx + out.x * fs * 0.35, lz + out.z * fs * 0.35, fmtLen((b - a) * 1000, units), fs, isUnit(a, b) ? SHEET_INK : SHEET_BLUE, { rot: along });
      }
      g += cg;
    }
    if (no) {
      const mx = (wall.start[0] + wall.end[0]) / 2, mz = (wall.start[1] + wall.end[1]) / 2;
      const off = t2 + 3.2 * den / 1000;
      const fs = FS.wallNo * den;
      g += text(X(mx + out.x * off) + out.x * fs * 0.35, Y(mz + out.z * off) + out.z * fs * 0.35, String(no), fs, SHEET_BLUE, { rot: along, ls: 0.1 * den });
    }
  });

  /* room outer dims (overall envelope) */
  const lane2 = 17 * den;
  g += dimLine(X(minX), Y(minZ) - lane2, X(maxX), Y(minZ) - lane2, fmtLen(spanW, units), den, { weight: 600 });
  g += dimLine(X(minX), Y(maxZ) + lane2, X(maxX), Y(maxZ) + lane2, fmtLen(spanW, units), den, { weight: 600, below: true });
  g += dimLine(X(minX) - lane2, Y(minZ), X(minX) - lane2, Y(maxZ), fmtLen(spanH, units), den, { weight: 600 });
  g += dimLine(X(maxX) + lane2, Y(minZ), X(maxX) + lane2, Y(maxZ), fmtLen(spanH, units), den, { weight: 600, side: "right" });

  return svgOpen(vx, vy, area.w * den, area.h * den, den, ` data-drawing="plan"`) + g + `</svg>`;
}

/** a dimension between two arbitrary points (red, ticks perpendicular, label rotated along) */
function obliqueDim(a, b, label, den, rotDeg, sideSign, fill, extra = "") {
  const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy) || 1;
  const ux = dx / L, uy = dy / L, px = -uy, py = ux;
  const t = 1.0 * den;
  let g = `<g stroke="${SHEET_RED}" stroke-width="${N(LW.dim * den)}" fill="none"${extra}>`;
  g += line(a[0], a[1], b[0], b[1], SHEET_RED, LW.dim * den);
  g += line(a[0] - px * t, a[1] - py * t, a[0] + px * t, a[1] + py * t, SHEET_RED, LW.dim * den);
  g += line(b[0] - px * t, b[1] - py * t, b[0] + px * t, b[1] + py * t, SHEET_RED, LW.dim * den);
  g += `</g>`;
  const fs = FS.dimSm * den;
  const off = 1.0 * den + fs * 0.35;
  g += text((a[0] + b[0]) / 2 + px * off * sideSign, (a[1] + b[1]) / 2 + py * off * sideSign, label, fs, fill || SHEET_INK, { rot: rotDeg });
  return g;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ELEVATION LEVELS (left ladder) — computed from the real units on the wall
 * ═══════════════════════════════════════════════════════════════════════════ */
export function elevationLevels(state, lib, wallId, opts = {}) {
  const walls = (state && state.walls) || [];
  const wall = walls.find((w) => w.id === wallId);
  if (!wall) return [];
  const kw = (state && state.kitchenWizard) || {};
  const wallH = wallHeightOf(wall);
  const members = elevMembershipOnWall(state, lib, wall, walls, "cabinets");
  const byId = new Map(((state && state.placements) || []).map((p) => [p.id, p]));
  const spans = (pred) => members.filter((m) => pred(m.def)).map((m) => { const p = byId.get(m.placementId); return p ? lhcVSpan(p, m.def) : [m.cabY - m.def.height / 2, m.cabY + m.def.height / 2]; });
  const bases = spans((d) => d.role === "cabinet-base");
  const talls = spans((d) => isTallDef(d));
  const highs = spans((d) => roleHigh(d.role));
  const plinth = ruleVal("PLINTH_H", kw);
  const levels = [];
  const push = (key, label, y0, y1) => { if (y1 - y0 > 0.004) levels.push({ key, label, y0, y1, mm: Math.round((y1 - y0) * 1000) }); };
  let maxTop = 0;
  if (bases.length || talls.length) push("plinth", "PLINTH", 0, plinth);
  if (bases.length) {
    const top = Math.max(...bases.map((s) => s[1]));
    push("base", "BASE UNIT", plinth, top);
    const firstBase = members.find((m) => m.def.role === "cabinet-base");
    const { spec } = worktopSpecFor(state, opts.blob, byId.get(firstBase.placementId), null);
    const wt = spec.thicknessMm / 1000;
    push("worktop", "WORKTOP", top, top + wt);
    maxTop = Math.max(maxTop, top + wt);
    if (highs.length) {
      const hb = Math.min(...highs.map((s) => s[0]));
      if (hb > top + wt + 0.004) push("space", "SPACE", top + wt, hb);
    }
  } else if (talls.length) {
    const top = Math.max(...talls.map((s) => s[1]));
    push("tall", "TALL UNIT", plinth, top);
    maxTop = Math.max(maxTop, top);
  }
  if (talls.length) maxTop = Math.max(maxTop, ...talls.map((s) => s[1]));
  if (highs.length) {
    const hb = Math.min(...highs.map((s) => s[0])), ht = Math.max(...highs.map((s) => s[1]));
    push("wall", "WALL UNIT", hb, ht);
    maxTop = Math.max(maxTop, ht);
  }
  if (levels.length && wallH - maxTop > 0.004) push("ceiling", "TO CEILING", maxTop, wallH);
  return levels.sort((a, b) => a.y0 - b.y0);
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ELEVATION — one wall, restyled to the reference
 * ═══════════════════════════════════════════════════════════════════════════ */
function hoodPath(u0, u1, v0, v1, X, Y) {
  // chimney (upper 45%) + flared canopy: M x h w v h l flare … (reference path shape)
  const w = u1 - u0, cw = Math.min(w * 0.4, 0.25);
  const cx = (u0 + u1) / 2, top = v1, bottom = v0, chimneyBottom = bottom + (top - bottom) * 0.42;
  const flare = Math.min(w * 0.5, 0.25);
  return `M ${N(X(cx - cw / 2))} ${N(Y(top))} h ${N(cw * 1000)} V ${N(Y(chimneyBottom))} l ${N(flare * 1000)} ${N((chimneyBottom - bottom) * 1000)} h ${N(-(cw + 2 * flare) * 1000)} l ${N(flare * 1000)} ${N(-(chimneyBottom - bottom) * 1000)} z`;
}

/** draws the projected primitives of one wall/frame (shared by wall elevations and the island faces) */
function drawPrims(ctx, cabinetsMap, extras, options = {}) {
  const { X, Y, den, blob, refs, byId, lib, showRefs = true } = ctx;
  let g = "";
  const glassOf = new Map();
  const kicks = [];
  cabinetsMap.forEach((entry, pid) => {
    const p = byId.get(pid);
    const def = p ? effDef(p, lib) : null;
    const glass = def ? isGlassFront(p, def, blob) : false;
    glassOf.set(pid, glass);
    const kind = def ? applianceKind(p, def) : null;
    const env = entry.envelope;
    if (kind === "hood" && env) {
      g += `<path d="${hoodPath(env.u0, env.u1, env.v0, env.v1, X, Y)}" fill="#ffffff" stroke="${SHEET_INK}" stroke-width="${N(LW.cab * den)}"/>`;
      return;
    }
    // unit outline (the nominal envelope) — thick
    if (env) g += rect(X(env.u0), Y(env.v1), (env.u1 - env.u0) * 1000, (env.v1 - env.v0) * 1000, SHEET_INK, LW.cab * den, "#ffffff");
    (entry.prims || []).forEach((pr) => {
      if (pr.kind === "swing" || pr.kind === "hidden") return;
      const x = X(pr.u0), y = Y(pr.v1), w = (pr.u1 - pr.u0) * 1000, h = (pr.v1 - pr.v0) * 1000;
      if (w <= 0 || h <= 0) return;
      if (pr.kind === "leg") {
        // CAD-level levelling feet (2026-09-30): the foot's own parts, outlined, under the ghost plinth
        (pr.parts || []).forEach((lp) => { g += rect(X(lp.u0), Y(lp.v1), (lp.u1 - lp.u0) * 1000, (lp.v1 - lp.v0) * 1000, SHEET_INK, LW.thin * den, "#ffffff"); });
        return;
      }
      if (pr.role === "kick") { kicks.push({ x, y, w, h, ghost: !!pr.ghost }); return; }   // merged below (the class carries the ghost flag since batch 12)
      switch (pr.cls) {
        case "elev-carcass": break;                       // the envelope already outlines the unit
        case "elev-front-leaf": g += rect(x, y, w, h, SHEET_INK, LW.thin * den, glass ? PANE : "none"); break;
        case "elev-front-drawer": g += rect(x, y, w, h, SHEET_INK, LW.thin * den, "none"); break;
        case "elev-handle-bar": g += rect(x, y, w, h, SHEET_INK, LW.thin * den, SHEET_INK); break;
        case "elev-gola-bar": g += pr.vertical ? line(x + w / 2, y, x + w / 2, y + h, SHEET_GOLD, LW.gola * den) : line(x, y + h / 2, x + w, y + h / 2, SHEET_GOLD, LW.gola * den); break;
        case "elev-side-panel": g += rect(x, y, w, h, SHEET_INK, LW.thin * den, "none"); break;
        case "elev-filler": g += rect(x, y, w, h, SHEET_INK, LW.thin * den, FILLER); break;
        case "elev-cut-hole": g += rect(x, y, w, h, SHEET_INK, LW.thin * den, "none", ` stroke-dasharray="${N(0.6 * den)} ${N(0.4 * den)}"`); break;
        case "elev-equip-glass": g += rect(x, y, w, h, SHEET_INK, LW.thin * den, PANE); break;
        case "elev-equipment": {
          g += rect(x, y, w, h, SHEET_INK, LW.thin * den, "none");
          const label = pr.label || "";
          if (label === "OVEN") {
            // control strip with knobs + display, glass door below (reference oven column)
            const sh = Math.min(h * 0.14, 110);
            g += rect(x + w * 0.05, y + h * 0.04, w * 0.9, sh, SHEET_INK, LW.thin * den, "none");
            g += `<circle cx="${N(x + w * 0.16)}" cy="${N(y + h * 0.04 + sh / 2)}" r="${N(Math.min(sh * 0.24, 26))}" fill="none" stroke="${SHEET_INK}" stroke-width="${N(LW.thin * den)}"/>`;
            g += `<circle cx="${N(x + w * 0.3)}" cy="${N(y + h * 0.04 + sh / 2)}" r="${N(Math.min(sh * 0.24, 26))}" fill="none" stroke="${SHEET_INK}" stroke-width="${N(LW.thin * den)}"/>`;
            g += rect(x + w * 0.45, y + h * 0.04 + sh * 0.3, w * 0.45, sh * 0.4, SHEET_INK, LW.thin * den, "none");
            g += rect(x + w * 0.12, y + h * 0.04 + sh + h * 0.08, w * 0.76, h * 0.5, SHEET_INK, LW.thin * den, PANE);
          } else if (label && label !== "HOOD" && w > 250) {
            g += text(x + w / 2, y + h * 0.5 + FS.labelSm * den * 0.35, label, FS.labelSm * den, SHEET_INK, { rot: h > w * 1.6 ? -90 : 0, ls: 0.08 * den });
          }
          break;
        }
        default: g += rect(x, y, w, h, SHEET_INK, LW.thin * den, "none");
      }
    });
    // drawer runner guides between fronts (the reference's faint grey verticals)
    if (env && def && !kind) {
      const rows = frontRowsOf(def, p && p.config);
      const drawers = rows ? rows.filter((r) => r.kind === "drawer").length : defDrawerCount(def, def.code || "");
      const doors = rows ? rows.filter((r) => r.kind === "door").reduce((s, r) => s + (r.doors || 1), 0) : defDoorCount(def, def.code || "");
      if (!drawers && doors === 1 && (env.u1 - env.u0) > 0.5 && (env.v1 - env.v0) > 0.5) {
        g += line(X((env.u0 + env.u1) / 2), Y(env.v1) + 50, X((env.u0 + env.u1) / 2), Y(env.v0) - 40, GREY, LW.guide * den);
      }
    }
    if (showRefs && env) {
      const r = refs && refs.get(pid);
      const cx = X((env.u0 + env.u1) / 2);
      const cy = isTallDef(def || {}) ? Y(env.v1) + (env.v1 - env.v0) * 1000 * 0.12 : (Y(env.v0) + Y(env.v1)) / 2;
      if (r) {
        g += text(cx, cy - 0.2 * den, r.ref, FS.ref * den, SHEET_GOLD);
        g += text(cx, cy + FS.sku * den * 1.7, r.sku, FS.sku * den, SHEET_GOLD_DK);
      } else if (def && def.code) {
        g += text(cx, cy + FS.sku * den * 0.35, def.code, FS.sku * den, SHEET_GOLD_DK);
      }
    }
  });
  // plinth: merge adjacent kick rects into one run rect + the faint recess line
  kicks.sort((a, b) => a.x - b.x);
  const runs = [];
  kicks.forEach((k) => {
    const last = runs[runs.length - 1];
    if (last && k.x <= last.x + last.w + 30 && Math.abs(k.y - last.y) < 5) { last.w = Math.max(last.w, k.x + k.w - last.x); last.ghost = last.ghost || k.ghost; return; }
    runs.push({ ...k });
  });
  runs.forEach((k) => {
    // a GHOST plinth (2026-09-30): dashed outline so the legs behind it stay readable, like the configurator
    g += rect(k.x, k.y, k.w, k.h, SHEET_INK, LW.thin * 1.6 * den, "none", k.ghost ? ` stroke-dasharray="${N(1.2 * den)} ${N(0.8 * den)}"` : "");
    g += line(k.x, k.y + Math.min(20, k.h * 0.2), k.x + k.w, k.y + Math.min(20, k.h * 0.2), GREY, LW.guide * den);
  });
  (extras || []).forEach((pr) => {
    const x = X(pr.u0), y = Y(pr.v1), w = (pr.u1 - pr.u0) * 1000, h = (pr.v1 - pr.v0) * 1000;
    if (w <= 0 || h <= 0) return;
    if (pr.kind === "gola") g += pr.vertical ? line(x + w / 2, y, x + w / 2, y + h, SHEET_GOLD, LW.gola * den) : line(x, y + h / 2, x + w, y + h / 2, SHEET_GOLD, LW.gola * den);
    else if (pr.cls === "elev-filler") {
      g += rect(x, y, w, h, SHEET_INK, LW.thin * den, FILLER);
      if (pr.widthMm) g += text(x + w / 2, y + h / 2, `F ${pr.widthMm}`, FS.sku * den, SHEET_INK, { rot: -90 });
    }
  });
  void options;
  return g;
}

export function buildElevationDrawingSVG(state, lib, wallId, opts = {}) {
  const units = opts.units || "mm";
  const area = opts.areaMm || ELEV_AREA_MM;
  const blob = opts.blob || null;
  const walls = (state && state.walls) || [];
  const wall = walls.find((w) => w.id === wallId) || walls[0];
  if (!wall) return null;
  const info = getWallPlacementInfo(wall, walls);
  const wallLen = info ? info.len : Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
  const wallH = wallHeightOf(wall);
  const members = elevMembershipOnWall(state, lib, wall, walls, "cabinets");
  const byId = new Map(((state && state.placements) || []).map((p) => [p.id, p]));
  const refs = unitRefs(state, lib);
  const levels = elevationLevels(state, lib, wall.id, { blob });

  const M = { l: 30, r: 8, t: 6, b: 16 };
  const den = sheetScaleFor(wallLen * 1000, wallH * 1000, area.w - M.l - M.r, area.h - M.t - M.b);
  const X = (u) => u * 1000;
  const Y = (h) => (wallH - h) * 1000;
  const vx = vbOrigin(0, wallLen * 1000, M.l, M.r, area.w, den);
  const vy = vbOrigin(0, wallH * 1000, M.t, M.b, area.h, den);

  let g = "";
  // ceiling (thin) + floor (thick) + wall edges
  const ext = 0.12 * 1000;
  g += line(X(0) - ext, Y(wallH), X(wallLen) + ext, Y(wallH), SHEET_INK, LW.dim * den);
  g += line(X(0), Y(wallH), X(0), Y(0), SHEET_INK, LW.wall * den);
  g += line(X(wallLen), Y(wallH), X(wallLen), Y(0), SHEET_INK, LW.wall * den);

  // openings (behind everything): window pane + sill, door frame + leaf + handle
  ((state && state.wallOpenings) || []).filter((o) => o.wallId === wall.id).forEach((op) => {
    const y0 = op.type === "window" ? (op.sill != null ? op.sill : 0.9) : 0;
    const hgt = op.height || (op.type === "window" ? 1.5 : 2.1);
    const x = X(op.u), y = Y(y0 + hgt), w = op.width * 1000, h = hgt * 1000;
    if (op.type === "window") {
      g += rect(x, y, w, h, SHEET_INK, LW.cab * den, "none");
      const inset = Math.min(60, w * 0.05);
      g += rect(x + inset, y + inset, w - 2 * inset, h - 2 * inset, SHEET_INK, LW.thin * den, PANE);
      g += line(x + w / 2, y + inset, x + w / 2, y + h - inset, SHEET_INK, LW.thin * den);
      g += line(x + inset, y + h / 2, x + w - inset, y + h / 2, SHEET_INK, LW.thin * 1.4 * den);
      g += rect(x - 40, y + h, w + 80, 40, SHEET_INK, LW.thin * 1.4 * den, "none");     // sill
      g += `<circle cx="${N(x + w - inset - 40)}" cy="${N(y + h * 0.55)}" r="${N(18)}" fill="none" stroke="${SHEET_INK}" stroke-width="${N(LW.thin * den)}"/>`;
    } else {
      g += rect(x - 60, y - 60, w + 120, h + 60, SHEET_INK, LW.thin * 1.4 * den, "none");   // frame
      g += rect(x, y, w, h, SHEET_INK, LW.cab * den, "none");                              // leaf
      const px = Math.min(100, w * 0.15), py = Math.min(200, h * 0.1);
      g += rect(x + px, y + py, w - 2 * px, h * 0.36, SHEET_INK, LW.thin * den, "none");
      g += rect(x + px, y + py + h * 0.42, w - 2 * px, h * 0.46, SHEET_INK, LW.thin * den, "none");
      const hx = op.hinge === "right" ? x + 90 : x + w - 90;
      g += `<circle cx="${N(hx)}" cy="${N(y + h * 0.52)}" r="${N(28)}" fill="none" stroke="${SHEET_INK}" stroke-width="${N(LW.thin * 1.4 * den)}"/>`;
    }
  });

  // TRUE projection of the 3D specs (cabinets, fronts, handles, gola, fillers)
  let drawing = { cabinets: new Map(), fillers: [], gola: [] };
  try { drawing = buildElevationDrawing({ state, lib, blob, wall, walls }); } catch (e) { drawing = { cabinets: new Map(), fillers: [], gola: [] }; }
  // fallback entries for members the projection could not build (envelope from state)
  members.forEach((m) => {
    if (drawing.cabinets.has(m.placementId)) return;
    const p = byId.get(m.placementId);
    const [y0, y1] = p ? lhcVSpan(p, m.def) : [m.cabY - m.def.height / 2, m.cabY + m.def.height / 2];
    drawing.cabinets.set(m.placementId, { prims: [], envelope: { u0: m.axisPos - m.def.width / 2, u1: m.axisPos + m.def.width / 2, v0: y0, v1: y1 }, role: m.def.role });
  });
  // worktop runs over adjacent base units (thickness from the technician spec) — drawn under the fronts
  {
    const runItems = [];
    const runMeta = new Map();
    members.forEach(({ def, axisPos, placementId }) => {
      const p = byId.get(placementId);
      if (!def || def.role !== "cabinet-base") return;
      const cfg = (p && p.config) || {};
      const [ry0, ry1] = p ? lhcVSpan(p, def) : [0, def.height];
      runItems.push({ id: placementId, wallId: wall.id, solo: !!((cfg.cuts && cfg.cuts.length) || (cfg.topCut && cfg.topCut.enabled)),
        s0: axisPos - def.width / 2, s1: axisPos + def.width / 2, hKey: Math.round(ry1 * 1000) + "," + Math.round(ry0 * 1000), ctKey: String(cfg.countertop || "") });
      runMeta.set(placementId, { top: ry1, p });
    });
    // merged runs (≥ 2 adjacent units — the SAME grouping the 3D slab uses) + every
    // uncovered single/solo unit keeps its own slab, so no base unit is ever topless
    const groups = worktopRunGroups(runItems).map((r) => ({ ...r, merged: true }));
    const covered = new Set();
    groups.forEach((r) => r.members.forEach((id) => covered.add(id)));
    const slabs = groups.concat(runItems.filter((it) => !covered.has(it.id)).map((it) => ({ wallId: it.wallId, ctKey: it.ctKey, s0: it.s0, s1: it.s1, members: [it.id], merged: false })));
    slabs.forEach((run, ri) => {
      const first = runMeta.get(run.members[0]);
      const { spec } = worktopSpecFor(state, blob, first && first.p, run.members);
      const t = spec.thicknessMm, ovL = spec.overhangSideLMm, ovR = spec.overhangSideRMm;
      const top = first ? first.top : 0;
      g += rect(X(run.s0) - ovL, Y(top) - t, (run.s1 - run.s0) * 1000 + ovL + ovR, t, SHEET_INK, LW.cab * 0.8 * den, "#ffffff");
      runSeams(run.s0, run.s1, sheetLenM(blob, run.ctKey || null)).forEach((sp) => {
        g += line(X(sp), Y(top) - t - 30, X(sp), Y(top) + 12, SHEET_INK, LW.thin * 1.4 * den);
      });
      // continuous-slab annotation (cut-list info): run number + length, muted, merged runs only
      if (run.merged) g += text(X((run.s0 + run.s1) / 2), Y(top) - t - 1.0 * den, `WORKTOP RUN ${ri + 1} · ${(Math.round((run.s1 - run.s0) * 100) / 100).toFixed(2)} m`, 1.5 * den, "#7a7a7a", { ls: 0.06 * den });
    });
  }
  g += drawPrims({ X, Y, den, blob, refs, byId, lib }, drawing.cabinets, [...(drawing.gola || []), ...(drawing.fillers || [])]);

  // floor line over everything
  g += line(X(0) - ext, Y(0), X(wallLen) + ext, Y(0), SHEET_INK, LW.floor * den);

  // LEVEL ladder (left): red vertical with ticks + right-aligned labels
  if (levels.length) {
    const lx = X(0) - 12 * den;
    const marks = new Set([0, wallH]);
    levels.forEach((l) => { marks.add(l.y0); marks.add(l.y1); });
    let lg = `<g stroke="${SHEET_RED}" stroke-width="${N(LW.dim * den)}" fill="none">` + line(lx, Y(wallH), lx, Y(0), SHEET_RED, LW.dim * den);
    marks.forEach((h) => { lg += line(lx - 1.0 * den, Y(h), lx + 1.0 * den, Y(h), SHEET_RED, LW.dim * den); });
    lg += `</g>`;
    levels.forEach((l) => {
      lg += text(lx - 1.8 * den, (Y(l.y0) + Y(l.y1)) / 2 + FS.level * den * 0.35, `${l.label} · ${fmtLen(l.mm, units)}`, FS.level * den, SHEET_INK, { anchor: "end" });
    });
    g += lg;
  }

  // RED width chain under the run: gaps + units + openings, then the total
  {
    const lower = members.filter((m) => !roleHigh(m.def.role));
    const row = lower.length ? lower : members;
    const bps = new Set([0, wallLen]);
    row.forEach((m) => { const e = drawing.cabinets.get(m.placementId); const u0 = e && e.envelope ? e.envelope.u0 : m.axisPos - m.def.width / 2; const u1 = e && e.envelope ? e.envelope.u1 : m.axisPos + m.def.width / 2; bps.add(Math.max(0, u0)); bps.add(Math.min(wallLen, u1)); });
    ((state && state.wallOpenings) || []).filter((o) => o.wallId === wall.id).forEach((o) => { bps.add(Math.max(0, o.u)); bps.add(Math.min(wallLen, o.u + o.width)); });
    const sorted = [...bps].sort((a, b) => a - b).filter((v, i, arr) => i === 0 || v - arr[i - 1] > 0.004).map(X);
    const y1 = Y(0) + 6.5 * den, y2 = Y(0) + 12.5 * den;
    g += dimChain(sorted, y1, den, (a, b) => ({ text: fmtLen(b - a, units), fill: SHEET_RED, weight: 500 }), { fs: FS.dim });
    g += dimLine(X(0), y2, X(wallLen), y2, fmtLen(wallLen * 1000, units), den, { fs: FS.total, weight: 600, fill: SHEET_RED });
  }

  return svgOpen(vx, vy, area.w * den, area.h * den, den, ` data-drawing="elevation" data-wall="${esc(wall.id)}"`) + g + `</svg>`;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * ISLAND — plan + long-face + seating-end elevations of one island cluster
 * ═══════════════════════════════════════════════════════════════════════════ */
/** a virtual state that stands the island members on a virtual wall along the island's back
 *  line, so the SAME spec pipeline (cabinetWorldPos → cabinetPartsSpec → projection) draws them */
function islandVirtualState(state, cl) {
  const f = cl.frame;
  const T = 0.1;
  const start = [f.sx + f.nx * (-T / 2), f.sz + f.nz * (-T / 2)];
  const end = [start[0] + f.dirX * cl.lengthM, start[1] + f.dirZ * cl.lengthM];
  const wall = { id: "__island__", start, end, height: Math.max(2.4, ...((state.walls || []).map((w) => wallHeightOf(w)))), thickness: T };
  const placements = cl.members.map(({ p, def }) => {
    const uC = p.x * f.dirX + p.z * f.dirZ;
    const u0 = uC - (def.width || 0) / 2 - cl.u0;
    const { island, x, z, dirX, dirZ, nx, nz, ...rest } = p;
    void island; void x; void z; void dirX; void dirZ; void nx; void nz;
    return { ...rest, wallId: wall.id, absLeft: u0 };
  });
  return { ...state, walls: [wall], placements, wallOpenings: [], symbolPlacements: [], decor: [] };
}

export function buildIslandDrawingSVGs(state, lib, opts = {}) {
  const units = opts.units || "mm";
  const blob = opts.blob || null;
  const clusters = islandClusters(state, lib);
  if (!clusters.length) return null;
  const cl = clusters[opts.index || 0];
  if (!cl) return null;
  const refs = unitRefs(state, lib);
  const { spec, over } = worktopSpecFor(state, blob, cl.members[0].p, cl.members.map((m) => m.p.id));
  const ovF = spec.overhangFrontMm / 1000, ovB = spec.overhangBackMm / 1000, ovL = spec.overhangSideLMm / 1000, ovR = spec.overhangSideRMm / 1000;
  const t = spec.thicknessMm / 1000;
  const L = cl.lengthM, D = cl.depthM;
  const topL = L + ovL + ovR, topD = D + ovF + ovB;
  // an island STANDS ON THE FLOOR whatever its members' nominal height (wall units grouped
  // as an island would otherwise float at the upper line): heights are measured from the
  // lowest member bottom, which the elevations draw as the floor line
  const spans = cl.members.map(({ p, def }) => lhcVSpan(p, def));
  const base = Math.min(...spans.map((s) => s[0]));
  const memberTop = Math.max(...spans.map((s) => s[1])) - base;
  const waterfall = !!(over.waterfall || over.waterfallL || over.waterfallR);
  const seatOverhangMm = Math.max(spec.overhangBackMm, spec.overhangFrontMm > 60 ? spec.overhangFrontMm : 0);
  const H = Math.max(memberTop + t + 0.05, 1.05);

  /* ONE scale for the sheet: each drawing is fitted to its OWN frame and the coarsest of the
   * three wins, so the plan and the two elevations are consistent AND fill their frames */
  const areaPlan = opts.planAreaMm || ISLAND_PLAN_AREA_MM, areaLong = opts.longAreaMm || ISLAND_ELEV_A_AREA_MM, areaEnd = opts.endAreaMm || ISLAND_ELEV_B_AREA_MM;
  const M_PLAN = { l: 10, r: 16, t: 12, b: 10 }, M_ELEV = { l: 6, r: 6, t: 3, b: 10.5 };   // 52.5 mm of a 66 mm frame for the height → a 1050 mm island fits 1:20
  const dens = {
    plan: sheetScaleFor(topL * 1000, topD * 1000, areaPlan.w - M_PLAN.l - M_PLAN.r, areaPlan.h - M_PLAN.t - M_PLAN.b),
    long: sheetScaleFor(topL * 1000, H * 1000, areaLong.w - M_ELEV.l - M_ELEV.r, areaLong.h - M_ELEV.t - M_ELEV.b),
    end: sheetScaleFor(topD * 1000, H * 1000, areaEnd.w - M_ELEV.l - M_ELEV.r, areaEnd.h - M_ELEV.t - M_ELEV.b),
  };
  const den = Math.max(dens.plan, dens.long, dens.end);

  /* ── PLAN (island frame: u to the right, front at the bottom) ── */
  let plan;
  {
    const area = areaPlan;
    const M = M_PLAN;
    const X = (u) => (u + ovL) * 1000, Y = (q) => (q + ovB) * 1000;
    const vx = vbOrigin(0, topL * 1000, M.l, M.r, area.w, den), vy = vbOrigin(0, topD * 1000, M.t, M.b, area.h, den);
    let g = "";
    g += rect(0, 0, topL * 1000, topD * 1000, SHEET_INK, LW.wall * den, "none");                     // worktop
    g += rect(X(0), Y(0), L * 1000, D * 1000, SHEET_RED, LW.dim * den, "none");                       // carcass footprint
    cl.members.forEach(({ p, def }, i) => {
      const uC = (p.x * cl.frame.dirX + p.z * cl.frame.dirZ) - cl.u0;
      const qC = (p.x * cl.frame.nx + p.z * cl.frame.nz) - cl.q0;
      const w = def.width || 0, d = def.depth || 0;
      const x0 = X(uC - w / 2), y0 = Y(qC - d / 2);
      if (i > 0) g += line(x0, Y(-ovB) - 40, x0, Y(D + ovF) + 40, SHEET_INK, LW.thin * 1.4 * den);
      const kind = applianceKind(p, def);
      const cx = x0 + w * 500, cy = y0 + d * 500;
      const drawers = defDrawerCount(def, def.code || ""), doors = defDoorCount(def, def.code || "");
      if (kind === "sink") {
        g += `<ellipse cx="${N(cx)}" cy="${N(cy)}" rx="${N(Math.min(w, 0.6) * 350)}" ry="${N(Math.min(d, 0.6) * 250)}" fill="#ffffff" stroke="${SHEET_INK}" stroke-width="${N(LW.cab * den)}"/>`;
        g += `<circle cx="${N(cx)}" cy="${N(y0 + d * 200)}" r="${N(34)}" fill="none" stroke="${SHEET_INK}" stroke-width="${N(LW.cab * den)}"/>`;
        g += text(cx, y0 + d * 1000 - 1.6 * den, "SINK", FS.label * den, SHEET_BLUE);
      } else if (kind === "dw" || kind === "hob" || kind === "oven") {
        g += rect(x0 + w * 80, y0 + d * 60, w * 840, d * 880, SHEET_GOLD, LW.cab * den, "none", ` stroke-dasharray="${N(0.7 * den)} ${N(0.5 * den)}"`);
        g += text(cx, cy + FS.label * den * 0.35, APPLIANCE_TEXT[kind], FS.label * den * 1.15, SHEET_GOLD);
      } else if (drawers > 0) {
        g += text(cx, cy + FS.label * den * 0.35, `DRAWERS ×${drawers}`, FS.label * den, SHEET_BLUE);
      } else if (doors > 0) {
        g += text(cx, cy + FS.label * den * 0.35, doors > 1 ? `DOORS ×${doors}` : "DOOR", FS.label * den, SHEET_BLUE);
      } else if (def.code) {
        g += text(cx, cy + FS.labelSm * den * 0.35, def.code, FS.labelSm * den, SHEET_GOLD_DK);
      }
    });
    const lane = 5 * den;
    g += dimLine(0, -lane, topL * 1000, -lane, fmtLen(topL * 1000, units), den, { fill: SHEET_RED, weight: 600, fs: FS.dim });
    g += dimLine(topL * 1000 + lane, 0, topL * 1000 + lane, topD * 1000, fmtLen(topD * 1000, units), den, { fill: SHEET_RED, weight: 600, side: "right", fs: FS.dim });
    g += text(topL * 500, topD * 1000 + 6 * den, `ISLAND ${cl.id} — PLAN`, FS.caption * den, SHEET_INK, { ls: 0.18 * den });
    plan = svgOpen(vx, vy, area.w * den, area.h * den, den, ` data-drawing="island-plan"`) + g + `</svg>`;
  }

  /* ── the projected faces (virtual wall → same spec pipeline as the 3D) ── */
  let longFace = "", endFace = "";
  const vState = islandVirtualState(state, cl);
  const vWall = vState.walls[0];
  const byId = new Map(vState.placements.map((p) => [p.id, p]));
  /* heights: v is a world height from the projection; the island floor is `base` */
  const Yh = (v) => (H - (v - base)) * 1000;
  const floorY = Yh(base), slabY = Yh(base + memberTop + t);
  {
    const area = areaLong;
    const M = M_ELEV;
    const X = (u) => (u + ovL) * 1000, Y = Yh;
    const vx = vbOrigin(0, topL * 1000, M.l, M.r, area.w, den), vy = vbOrigin(0, H * 1000, M.t, M.b, area.h, den);
    let drawing = { cabinets: new Map(), fillers: [], gola: [] };
    try { drawing = buildElevationDrawing({ state: vState, lib, blob, wall: vWall, walls: [vWall] }); } catch (e) { /* fall back to envelopes */ }
    cl.members.forEach(({ p, def }) => {
      if (drawing.cabinets.has(p.id)) return;
      const vp = byId.get(p.id);
      const [y0, y1] = lhcVSpan(vp, def);
      drawing.cabinets.set(p.id, { prims: [], envelope: { u0: vp.absLeft, u1: vp.absLeft + def.width, v0: y0, v1: y1 }, role: def.role });
    });
    let g = "";
    // worktop slab (+ waterfall legs when the run has them)
    g += rect(0, slabY, topL * 1000, t * 1000, SHEET_INK, LW.cab * 0.8 * den, "#ffffff");
    if (waterfall) {
      g += rect(0, slabY, t * 1000, (memberTop + t) * 1000, SHEET_INK, LW.cab * 0.8 * den, "#ffffff");
      g += rect(topL * 1000 - t * 1000, slabY, t * 1000, (memberTop + t) * 1000, SHEET_INK, LW.cab * 0.8 * den, "#ffffff");
      g += text(0.6 * den, slabY - 1.0 * den, "WATERFALL", FS.labelSm * den, SHEET_GOLD, { anchor: "start" });
    }
    g += drawPrims({ X, Y, den, blob, refs, byId, lib, showRefs: false }, drawing.cabinets, drawing.gola || []);
    // drawer / door labels inside the fronts (blue, as the reference)
    drawing.cabinets.forEach((e, pid) => {
      const p = byId.get(pid); const def = p ? effDef(p, lib) : null;
      if (!def || !e.envelope) return;
      const kind = applianceKind(p, def);
      const drawers = defDrawerCount(def, def.code || "");
      const label = kind === "dw" ? "DW" : kind === "sink" ? "SINK" : drawers > 0 ? "DRAWER" : (defDoorCount(def, def.code || "") > 0 ? "DOOR" : "");
      if (label) g += text(X((e.envelope.u0 + e.envelope.u1) / 2), (Y(e.envelope.v0) + Y(e.envelope.v1)) / 2, label, FS.labelSm * den, kind === "dw" ? SHEET_GOLD : SHEET_BLUE);
    });
    g += line(-0.4 * den * 10, floorY, topL * 1000 + 0.4 * den * 10, floorY, SHEET_INK, LW.floor * den, ` data-floor="1"`);
    g += dimLine(0, floorY + 4.5 * den, topL * 1000, floorY + 4.5 * den, fmtLen(topL * 1000, units), den, { fill: SHEET_RED, weight: 600, fs: FS.dimSm, below: true });
    g += text(topL * 500, floorY + 9.6 * den, "ELEVATION A — LONG FACE", FS.labelSm * den, SHEET_INK, { ls: 0.12 * den });
    longFace = svgOpen(vx, vy, area.w * den, area.h * den, den, ` data-drawing="island-long"`) + g + `</svg>`;
  }
  {
    const area = areaEnd;
    const M = M_ELEV;
    // viewer at the right end looking left: the FRONT is on the left, the seating overhang at the right
    const X = (u) => (u + ovF) * 1000, Y = Yh;
    const vx = vbOrigin(0, topD * 1000, M.l, M.r, area.w, den), vy = vbOrigin(0, H * 1000, M.t, M.b, area.h, den);
    const f = cl.frame;
    const frame2 = { sx: f.sx + f.nx * D, sz: f.sz + f.nz * D, dirX: f.dirZ, dirZ: -f.dirX, nx: f.dirX, nz: f.dirZ, len: D };
    let specs = [];
    try { specs = buildCabinetSpecs(vState, lib, blob) || []; } catch (e) { specs = []; }
    try { applyGolaRuns(specs, vState, lib, blob); } catch (e) { /* gola never breaks the face */ }
    const cabinets = new Map();
    const extras = [];
    specs.forEach((spec) => {
      const p = byId.get(spec.moduleId);
      if (!p) { if (spec.role === "gola-run") projectSpec(frame2, spec).forEach((pr) => extras.push(pr)); return; }
      const def = effDef(p, lib);
      if (!def) return;
      let vis = null;
      try { vis = p.config ? resolveConfigVisual(blob || {}, p.type, p.config) : null; } catch (e) { vis = null; }
      cabinets.set(p.id, { prims: projectSpec(frame2, spec, { def, config: p.config || {}, blob, vis }), envelope: spec.modelEnvelope ? projectBox(frame2, spec.modelEnvelope) : null, role: def.role });
    });
    let g = "";
    g += rect(0, slabY, topD * 1000, t * 1000, SHEET_INK, LW.cab * 0.8 * den, "#ffffff");
    g += drawPrims({ X, Y, den, blob, refs, byId, lib, showRefs: false }, cabinets, extras);
    g += text(X(D / 2), (floorY + Yh(base + memberTop)) / 2, "CARCASS", FS.labelSm * den, SHEET_BLUE);
    if (seatOverhangMm >= 100) {
      const ox0 = X(D), ox1 = X(D + ovB);
      g += line(ox0, Yh(base + memberTop) + 8 * den / 10, ox1, Yh(base + memberTop) + 8 * den / 10, SHEET_BLUE, LW.dim * den, ` stroke-dasharray="${N(0.6 * den)} ${N(0.4 * den)}"`);
      g += text((ox0 + ox1) / 2, slabY - 1.2 * den, `${fmtLen(seatOverhangMm, units)} OVERHANG`, FS.labelSm * 0.9 * den, SHEET_GOLD);
      if (seatOverhangMm >= 250) {
        const sx = (ox0 + ox1) / 2;
        g += `<ellipse cx="${N(sx)}" cy="${N(Yh(base + 0.65))}" rx="${N(120)}" ry="${N(34)}" fill="none" stroke="${SHEET_INK}" stroke-width="${N(LW.thin * 1.4 * den)}"/>`;
        g += line(sx, Yh(base + 0.65), sx, floorY, SHEET_INK, LW.thin * 1.4 * den);
      }
    }
    g += line(-0.4 * den * 10, floorY, topD * 1000 + 0.4 * den * 10, floorY, SHEET_INK, LW.floor * den, ` data-floor="1"`);
    g += dimLine(0, floorY + 4.5 * den, topD * 1000, floorY + 4.5 * den, fmtLen(topD * 1000, units), den, { fill: SHEET_RED, weight: 600, fs: FS.dimSm, below: true });
    g += text(topD * 500, floorY + 9.6 * den, "ELEVATION B — SEATING END", FS.labelSm * den, SHEET_INK, { ls: 0.12 * den });
    endFace = svgOpen(vx, vy, area.w * den, area.h * den, den, ` data-drawing="island-end"`) + g + `</svg>`;
  }

  const drawersTotal = cl.members.reduce((s, { def }) => s + defDrawerCount(def, def.code || ""), 0);
  return {
    id: cl.id, plan, longFace, endFace,
    lengthMm: Math.round(topL * 1000), depthMm: Math.round(topD * 1000), thicknessMm: spec.thicknessMm,
    waterfall, seatOverhangMm, drawers: drawersTotal,
    members: cl.members.map(({ p, def }) => ({ id: p.id, ref: (refs.get(p.id) || {}).ref, sku: def.code || "—", name: def.name || def.code || p.type, kind: applianceKind(p, def) })),
    scaleDen: den, dens, baseM: base, heightM: memberTop,
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
 * legacy exports — DXF R12 floor plan + CV schedule CSV (other code imports them)
 * ═══════════════════════════════════════════════════════════════════════════ */
function dxfPolyline(pts, layer) {
  let s = `0\nPOLYLINE\n8\n${layer}\n66\n1\n70\n1\n`;
  pts.forEach(([x, z]) => { s += `0\nVERTEX\n8\n${layer}\n10\n${MM(x)}\n20\n${MM(-z)}\n30\n0\n`; });
  return s + `0\nSEQEND\n`;
}
function dxfLine(a, b, layer) {
  return `0\nLINE\n8\n${layer}\n10\n${MM(a[0])}\n20\n${MM(-a[1])}\n30\n0\n11\n${MM(b[0])}\n21\n${MM(-b[1])}\n31\n0\n`;
}
function dxfText(x, z, h, txt, layer) {
  return `0\nTEXT\n8\n${layer}\n10\n${MM(x)}\n20\n${MM(-z)}\n30\n0\n40\n${h}\n1\n${txt}\n72\n1\n11\n${MM(x)}\n21\n${MM(-z)}\n`;
}

/** Floor-plan DXF (R12 ASCII): WALLS / OPENINGS / CABINETS / TEXT layers, mm. */
export function buildPlanDXF(state, lib) {
  const walls = (state && state.walls) || [];
  const placements = (state && state.placements) || [];
  let ents = "";
  getWallPolygons(walls).forEach((r) => { ents += dxfPolyline(r.polygon, "WALLS"); });
  ((state && state.wallOpenings) || []).forEach((op) => {
    const q = openingQuad(op, walls);
    if (q) { ents += dxfPolyline(q.pts, "OPENINGS"); ents += dxfLine(q.a, q.b, "OPENINGS"); }
  });
  placements.forEach((p) => {
    const def = effDef(p, lib);
    if (!def) return;
    const c = planCorners(p, def, walls);
    if (!c) return;
    ents += dxfPolyline(c.pts, "CABINETS");
    const cx = c.pts.reduce((s, q) => s + q[0], 0) / 4, cz = c.pts.reduce((s, q) => s + q[1], 0) / 4;
    ents += dxfText(cx, cz, 60, def.code || p.type, "TEXT");
  });
  const layers = ["WALLS", "OPENINGS", "CABINETS", "TEXT"]
    .map((n, i) => `0\nLAYER\n2\n${n}\n70\n0\n62\n${i + 1}\n6\nCONTINUOUS\n`).join("");
  return "0\nSECTION\n2\nHEADER\n9\n$INSUNITS\n70\n4\n0\nENDSEC\n"
    + `0\nSECTION\n2\nTABLES\n0\nTABLE\n2\nLAYER\n70\n4\n${layers}0\nENDTAB\n0\nENDSEC\n`
    + `0\nSECTION\n2\nENTITIES\n${ents}0\nENDSEC\n0\nEOF\n`;
}

/** Unit-schedule CSV (CV export): cut-list style, mm dims + pricing. */
export function buildScheduleCSV(state, lib, units = "mm") {
  const rows = drawingUnitRows(state, lib);
  const q = (s) => `"${String(s == null ? "" : s).replace(/"/g, '""')}"`;
  const out = [["#", "Code", "Description", `Width (${units})`, `Height (${units})`, `Depth (${units})`, "Qty", "Unit Price (EUR)", "Total (EUR)"].join(",")];
  rows.forEach((r, i) => {
    const unit = r.qty ? r.price / r.qty : 0;
    out.push([i + 1, q(r.def.code || ""), q(r.def.name || ""), dwpFmt(r.def.width, units),
      dwpFmt(r.def.height, units), dwpFmt(r.def.depth, units), r.qty, unit.toFixed(2), r.price.toFixed(2)].join(","));
  });
  out.push(["", "", q("TOTAL"), "", "", "", rows.reduce((s, r) => s + r.qty, 0), "",
    rows.reduce((s, r) => s + r.price, 0).toFixed(2)].join(","));
  return out.join("\r\n");
}

/** the scale denominator a builder chose (data-scale on the root svg) */
export function svgScaleOf(svg) {
  const m = /data-scale="(\d+)"/.exec(svg || "");
  return m ? +m[1] : null;
}
export { wallFrame };
